import {useCallback, useEffect, useRef, useState} from 'react'
import {PriceSearchDetail, STATUS_FINAIS} from '../../../types/priceResearch'

export const RESEARCH_RECONCILE_MS = 10000
type Load = (id: string, signal: AbortSignal) => Promise<PriceSearchDetail>

// One request per selected view. Notifications coalesce with an in-flight GET;
// a recursive timer reconciles missed pushes without creating a request backlog.
export function useResearchDetail(id: string | null, load: Load,
    onData: (data: PriceSearchDetail) => void) {
    const [detail, store] = useState<PriceSearchDetail | null>(null)
    const [loading, setLoading] = useState(false)
    const selected = useRef(id)
    selected.current = id
    const current = useRef<{
        id: string; stopped: boolean; version: number; terminal: boolean
        snapshot?: PriceSearchDetail
        controller?: AbortController; timer?: ReturnType<typeof setTimeout>
        pending?: Promise<void>; refresh: () => Promise<void>
    } | null>(null)

    const setDetail = useCallback((data: PriceSearchDetail | null) => {
        const state = current.current
        if (!data || !state || state.stopped || selected.current !== data.id) return
        // A command/review response supersedes any older GET already in flight.
        state.version++
        state.snapshot = data
        state.terminal = STATUS_FINAIS.includes(data.status)
        if (state.terminal) clearTimeout(state.timer)
        store(data)
        onData(data)
    }, [onData])

    useEffect(() => {
        store(null)
        if (!id) { setLoading(false); return }
        const state: NonNullable<typeof current.current> = {
            id, stopped: false, version: 0, terminal: false,
            refresh: async () => {},
        }
        current.current = state
        const valid = () => !state.stopped && selected.current === id
        state.refresh = () => {
            if (!valid()) return Promise.resolve()
            if (state.pending) return state.pending
            clearTimeout(state.timer)
            const version = state.version
            const controller = new AbortController()
            state.controller = controller
            state.pending = (async () => {
                try {
                    const data = await load(id, controller.signal)
                    if (!valid() || controller.signal.aborted || version !== state.version || data.id !== id) return
                    // Also reject stale snapshots returned by replicas/caches.
                    if (state.snapshot && state.snapshot.updated_at > data.updated_at) return
                    state.snapshot = data
                    store(data)
                    state.terminal = STATUS_FINAIS.includes(data.status)
                    onData(data)
                } catch (error) {
                    if (valid() && !controller.signal.aborted) console.error('Erro ao reconciliar pesquisa:', error)
                } finally {
                    state.pending = undefined
                    if (valid()) {
                        setLoading(false)
                        if (!state.terminal) state.timer = setTimeout(() => void state.refresh(), RESEARCH_RECONCILE_MS)
                    }
                }
            })()
            return state.pending
        }
        setLoading(true)
        void state.refresh()
        const regain = () => { if (!state.terminal) void state.refresh() }
        window.addEventListener('focus', regain)
        window.addEventListener('online', regain)
        return () => {
            state.stopped = true
            clearTimeout(state.timer)
            state.controller?.abort()
            window.removeEventListener('focus', regain)
            window.removeEventListener('online', regain)
        }
    }, [id, load, onData])

    // Explicit notifications can announce reanalysis started elsewhere after a
    // terminal snapshot. Only automatic timer/focus/online reads stop there.
    const refresh = useCallback(async (target: string, force = true) => {
        if (current.current?.id === target && selected.current === target && (force || !current.current.terminal)) await current.current.refresh()
    }, [])
    return {detail: detail?.id === id ? detail : null, setDetail, loading, refresh}
}
